import { useState, type DragEvent } from "react";
import { UPLOAD_ACCEPT_ATTR } from "@/config";

type Props = {
  onFiles: (files: FileList | File[]) => void;
  disabled?: boolean;
};

export function DropZone({ onFiles, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    if (e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`rounded-lg border-2 border-dashed p-6 text-center shadow-card transition-colors duration-fast sm:p-10 ${
        disabled
          ? "border-ink/20 bg-cream opacity-60"
          : dragOver
            ? "border-coral bg-coral/5"
            : "border-ink/20 bg-cream hover:border-coral/60 hover:bg-cream-deep/50"
      }`}
    >
      <p className="font-serif text-xl text-primary">Drop files or folders here</p>
      <p className="mt-2 text-body-sm text-secondary">
        PDF, Office documents, images, and ZIP archives. Folder structure is preserved.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <label
          className={`cursor-pointer rounded-md bg-coral px-5 py-2.5 font-mono text-button text-surface transition-opacity duration-fast hover:opacity-90 ${
            disabled ? "pointer-events-none opacity-60" : ""
          }`}
        >
          Choose files
          <input
            type="file"
            multiple
            accept={UPLOAD_ACCEPT_ATTR}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => e.target.files && onFiles(e.target.files)}
          />
        </label>
        <label
          className={`cursor-pointer rounded-md border border-ink/20 bg-app px-5 py-2.5 font-mono text-button text-primary transition-colors duration-fast hover:bg-cream-deep ${
            disabled ? "pointer-events-none opacity-60" : ""
          }`}
        >
          Choose folder
          <input
            type="file"
            multiple
            accept={UPLOAD_ACCEPT_ATTR}
            className="sr-only"
            disabled={disabled}
            // @ts-expect-error webkitdirectory is non-standard but widely supported
            webkitdirectory=""
            onChange={(e) => e.target.files && onFiles(e.target.files)}
          />
        </label>
      </div>
    </div>
  );
}
